"use client"

import { useState, useEffect } from "react"
import { UserPlus, UserMinus, Loader2 } from "lucide-react"

interface FollowButtonProps {
  userId: string
  initialIsFollowing?: boolean
}

export default function FollowButton({ userId, initialIsFollowing = false }: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing)
  const [isLoading, setIsLoading] = useState(false)
  const [isChecking, setIsChecking] = useState(!initialIsFollowing)

  // Check follow status if not provided
  useEffect(() => {
    if (!initialIsFollowing) {
      checkFollowStatus()
    }
  }, [initialIsFollowing])

  const checkFollowStatus = async () => {
    setIsChecking(true)
    try {
      const response = await fetch(`/api/follow/check?followingId=${userId}`)
      const data = await response.json()

      if (response.ok) {
        setIsFollowing(data.isFollowing)
      }
    } catch (error) {
      console.error("Error checking follow status:", error)
    } finally {
      setIsChecking(false)
    }
  }

  const handleFollow = async () => {
    if (isLoading) return

    setIsLoading(true)

    try {
      if (isFollowing) {
        // Unfollow
        const response = await fetch(`/api/follow/${userId}`, {
          method: "DELETE",
        })

        if (response.ok) {
          setIsFollowing(false)
        }
      } else {
        // Follow
        const response = await fetch("/api/follow", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ followingId: userId }),
        })

        if (response.ok) {
          setIsFollowing(true)
        }
      }
    } catch (error) {
      console.error("Error following/unfollowing:", error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isChecking) {
    return (
      <button
        disabled
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400"
      >
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Checking...
      </button>
    )
  }

  if (isFollowing) {
    return (
      <button
        onClick={handleFollow}
        disabled={isLoading}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserMinus className="h-4 w-4 mr-2" />}
        Unfollow
      </button>
    )
  }

  return (
    <button
      onClick={handleFollow}
      disabled={isLoading}
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
      Follow
    </button>
  )
}

